import { useState } from "react";
import { ListX } from "lucide-react";

import type { LookupResponse, ReturnReasonCode } from "@sirdab/returns-shared";
import { EmptyState, Textarea } from "@sirdab/ui";

import { ChoiceGroup, ChoiceOption, Field, StepActions } from "../components";
import { useFlow } from "../flow";
import { useMessages } from "../i18n";
import { RequireLookup } from "./shared";
import { StepScaffold } from "./StepScaffold";

/**
 * Step 3 of docs/08 — the reason, one radio list per selected line.
 *
 * The list is `policy.reasons` verbatim. It is NEVER hardcoded and never filtered here:
 * `toPublicPolicy()` already projects only the reasons the merchant enabled (policy check
 * 6), so a disabled reason has no path onto this screen. Labels are the merchant's own
 * bilingual text, rendered — not translated.
 *
 * Sub-reasons render only where the merchant configured them: `subReasons` is optional on
 * `ReasonConfig` and projected verbatim onto `PublicPolicy.reasons`, so absent and `[]`
 * both mean "no sub-reason picker for this reason" and nobody is forced to pick one.
 *
 * `other` is the one reason that needs the shopper's words: it opens a required note
 * right under the choice, and the step will not continue until every `other` line has
 * one. The server enforces the same rule (`note_required`), so this is a courtesy, not
 * the decision. The note is the same `draft.note` the evidence step shows as optional,
 * so switching reasons later never loses what was typed.
 */
const OTHER: ReturnReasonCode = "other";

function noteMissing(draft: {
  reasonCode: string | null;
  note: string | null;
}): boolean {
  return draft.reasonCode === OTHER && (draft.note ?? "").trim() === "";
}
function Body({ lookup }: { lookup: LookupResponse }): React.JSX.Element {
  const { t, text } = useMessages();
  const { lines: drafts, update, next, back } = useFlow();
  const [touchedNotes, setTouchedNotes] = useState<Set<string>>(new Set());

  const reasons = lookup.policy.reasons;
  if (reasons.length === 0) {
    return (
      <EmptyState
        icon={ListX}
        title={t("reason.none_offered")}
        className="py-8"
      />
    );
  }
  if (drafts.length === 0) {
    return (
      <EmptyState
        icon={ListX}
        title={t("validation.select_at_least_one")}
        className="py-8"
      />
    );
  }

  function setReason(lineRef: string, code: ReturnReasonCode): void {
    update({
      lines: drafts.map((draft) =>
        draft.lineRef === lineRef
          ? { ...draft, reasonCode: code, subReason: null }
          : draft,
      ),
    });
  }

  function setSubReason(lineRef: string, code: string): void {
    update({
      lines: drafts.map((draft) =>
        draft.lineRef === lineRef ? { ...draft, subReason: code } : draft,
      ),
    });
  }

  function setNote(lineRef: string, note: string): void {
    update({
      lines: drafts.map((draft) =>
        draft.lineRef === lineRef ? { ...draft, note } : draft,
      ),
    });
  }

  function touchNote(lineRef: string): void {
    setTouchedNotes((current) => new Set(current).add(lineRef));
  }

  const complete =
    drafts.every((draft) => draft.reasonCode !== null) &&
    !drafts.some(noteMissing);

  return (
    <div className="flex flex-col gap-6">
      <p className="text-body text-muted-foreground">
        {t("reason.select_hint")}
      </p>

      {drafts.map((draft) => {
        const line = lookup.order.lines.find(
          (entry) => entry.lineRef === draft.lineRef,
        );
        const subReasons =
          reasons.find((entry) => entry.code === draft.reasonCode)
            ?.subReasons ?? [];
        return (
          <fieldset key={draft.lineRef} className="flex flex-col gap-2">
            <legend className="text-label pb-2 text-foreground">
              {line === undefined ? draft.lineRef : text(line.productName)}
            </legend>
            <ChoiceGroup
              value={draft.reasonCode}
              onChange={(code) =>
                setReason(draft.lineRef, code as ReturnReasonCode)
              }
            >
              {reasons.map((reason) => (
                <ChoiceOption
                  key={reason.code}
                  value={reason.code}
                  checked={draft.reasonCode === reason.code}
                  label={text(reason.label)}
                />
              ))}
            </ChoiceGroup>

            {draft.reasonCode === OTHER ? (
              <div className="mt-1 ps-3" data-other-note={draft.lineRef}>
                <Field
                  label={t("field.other_note.label")}
                  hint={t("field.other_note.hint")}
                  error={
                    touchedNotes.has(draft.lineRef) && noteMissing(draft)
                      ? t("validation.other_note_required")
                      : undefined
                  }
                  required
                >
                  {(props) => (
                    <Textarea
                      {...props}
                      rows={2}
                      value={draft.note ?? ""}
                      onChange={(event) =>
                        setNote(draft.lineRef, event.target.value)
                      }
                      onBlur={() => touchNote(draft.lineRef)}
                    />
                  )}
                </Field>
              </div>
            ) : null}

            {subReasons.length > 0 ? (
              <fieldset className="mt-1 flex flex-col gap-2 ps-3">
                <legend className="text-caption text-muted-foreground pb-1">
                  {t("field.sub_reason.label")}
                </legend>
                <ChoiceGroup
                  value={draft.subReason}
                  onChange={(code) => setSubReason(draft.lineRef, code)}
                >
                  {subReasons.map((sub) => (
                    <ChoiceOption
                      key={sub.code}
                      value={sub.code}
                      checked={draft.subReason === sub.code}
                      label={text(sub.label)}
                    />
                  ))}
                </ChoiceGroup>
              </fieldset>
            ) : null}
          </fieldset>
        );
      })}

      {!complete ? (
        <p className="text-caption-quiet text-muted-foreground">
          {drafts.some(noteMissing) &&
          drafts.every((draft) => draft.reasonCode !== null)
            ? t("validation.other_note_required")
            : t("validation.reason_required")}
        </p>
      ) : null}

      <StepActions onNext={next} onBack={back} nextDisabled={!complete} />
    </div>
  );
}

export function ReasonScreen(): React.JSX.Element {
  return (
    <StepScaffold
      step="reason"
      titleKey="screen.reason.title"
      descriptionKey="screen.reason.description"
    >
      <RequireLookup>{(lookup) => <Body lookup={lookup} />}</RequireLookup>
    </StepScaffold>
  );
}
